import { useEffect } from 'react'
import type { ReactElement } from 'react'
import { Helmet } from 'react-helmet-async'
import Header from 'src/components/common/Header'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { graphql } from 'gatsby'

import { getCookie } from './utils/cookies'
import { usePageViewEvent } from './utils/gtmEvents'
import { UserDataProvider } from './components/contexts/UserDataContext'
import { ThemeProvider } from './components/contexts/ThemeContext'
import Footer from './components/restructure/common/Footer'
import AccountFooter from './components/account/Footer'
import { WishlistProvider } from './components/contexts/wishlist-context'
import { CheckoutProvider } from './sdk/checkout/context/checkout'
import InitialPopUps from './components/PopUps/InitialPopUps'
import { ModalSheet } from './components/ui/ModalSheet/ModalSheet'
import { getLoginOriginType } from './utils/amplitude/getLoginOriginType'
import { SourceTracker } from './components/common/SourceTracker/SourceTracker'
import dynamicYieldEvents from './utils/dynamicYield/dynamicYieldEvents'
import dynamicYieldOtherView from './utils/dynamicYield/dynamicYieldOtherPageView'
import { resolveFavoriteNames } from './utils/account/masterDataFavorites'
import { ingestPendingDyOfflinePurchases } from './utils/dynamicYield/ingestDyOfflinePurchases'
import {
  mapUserDecathlonToDyPayload,
  upsertDyUserData,
} from './utils/dynamicYield/upsertDyUserData'
import { AddressProvider } from './components/checkout/steps/shipping/context/address-context'
import { dynamicYieldContextPage } from './utils/dynamicYield/dynamicYieldContextPage'
import { Statsig } from './sdk/statsig'
import { CartFeedbackProvider } from './components/contexts/CartFeedbackContext'
import CartFeedback from './components/cart/CartFeedback'
import StatsigExposure from './components/common/StatsigExposure/StatsigExposure'
import { useAccountContext } from './components/account/context'
import { setDyIdServer } from './utils/dynamicYield/setDyIdServer'
import { GlobalContextProvider } from './components/contexts/GlobalContext'
import { useDynamicYieldEvents } from './sdk/hooks/useDynamicYieldEvents'
import { useLoadDynamicYieldAfterHydration } from './sdk/hooks/useLoadDynamicYieldAfterHydration'
import { useSwapImagesABTest } from './sdk/hooks/useSwapImagesABTest'
import { PixPaymentReminder } from './components/cart/PixPaymentReminder'
import { exposeDYCampaingsOnAmplitude } from './utils/amplitude/exposeDYCampaingsOnAmplitude'
import { usePageType } from './hooks/usePageType'

export type LayoutProps = {
  children: ReactElement
  path: string
  location: Location
  serverData: {
    pageType?: string
    searchParams?: {
      term: string
    }
  }
  '*'?: string
  slug?: string
}

function Layout({ children, path, location, ...props }: LayoutProps) {
  const { pathname, href, search } = location

  const pageData = usePageType({ ...props, location })

  const { userDecathlon } = useAccountContext()
  const mode = new URLSearchParams(search).get('mode')
  const statsigEnvTier = new URLSearchParams(search).get('statsig_env_tier') as
    | 'production'
    | 'staging'
    | 'development'

  const hideSiteHeader = [
    'member',
    'checkout',
    'login',
    'saldo-cartao-presente',
    'blog',
  ].some((item) => pathname.includes(item))

  const hideSiteFooter = [
    'member',
    'checkout',
    'login',
    'saldo-cartao-presente',
  ].some((item) => pathname.includes(item))

  const isAccountFooter = pathname.includes('account')

  const url = href ? new URL(href) : null

  const paramOrderFormId = url?.searchParams.get('orderFormId')

  usePageViewEvent()

  useDynamicYieldEvents(pageData)

  useLoadDynamicYieldAfterHydration()

  useSwapImagesABTest()

  useEffect(() => {
    exposeDYCampaingsOnAmplitude()
    const dyidServer = getCookie('_dyid_server')

    if (dyidServer) {
      return
    }

    setDyIdServer()
  }, [])

  useEffect(() => {
    if (statsigEnvTier) {
      Statsig.setStatsigEnvTier(statsigEnvTier)
    }

    Statsig.initialize({
      apiKey: 'client-H2CHANLHAQjJyKY81gja6XKpH2evNrXZdZJ6md28mwj',
    })
  }, [statsigEnvTier])

  useEffect(() => {
    if (pathname === '/') {
      dynamicYieldContextPage({ page: 'HOMEPAGE' })

      return
    }

    if (pathname.includes('/checkout/cart')) {
      return
    }

    if (!children) {
      return
    }

    dynamicYieldOtherView(path)
  }, [children, path, pathname])

  useEffect(() => {
    const el = document.getElementsByClassName('chatbot-sc-launcher')

    if (pathname !== '/atendimento' && el.length > 0) {
      el[0].remove()
    }
  }, [pathname])

  useEffect(() => {
    const isLoginSuccess = window.location.href.includes('loginSuccess')

    if (!isLoginSuccess) {
      return
    }

    const loginOriginPage = getCookie('loginURL')

    if (!userDecathlon) {
      return
    }

    dispatchAmplitudeEvent({
      eventName: 'Login Completed',
      eventData: {
        ...getStandardObject(pageData),
        'login method': 'email',
        'login trigger': getLoginOriginType(loginOriginPage),
        'user id': userDecathlon?.getUserProfile?.sub,
      },
    })

    dynamicYieldEvents([
      {
        name: 'Login',
        properties: {
          dyType: 'login-v1',
          cuidType: 'customer_id',
          cuid: userDecathlon.getUserProfile.sub,
        },
      },
    ])

    ingestPendingDyOfflinePurchases(userDecathlon)

    const payload = mapUserDecathlonToDyPayload(userDecathlon)

    if (!payload) {
      return
    }

    const sentKey = `dy-userdata-sent:${payload.cuid}`

    if (sessionStorage.getItem(sentKey)) {
      return
    }

    sessionStorage.setItem(sentKey, '1')

    const ingestDyUserData = async () => {
      const extras = await resolveFavoriteNames(userDecathlon)
      const nextPayload = mapUserDecathlonToDyPayload(userDecathlon, extras)

      if (nextPayload) {
        await upsertDyUserData(nextPayload)
      }
    }

    ingestDyUserData()
  }, [pageData, userDecathlon])

  useEffect(() => {
    dispatchAmplitudeEvent({
      eventName: 'Page Viewed',
      eventData: {
        ...getStandardObject(pageData),
      },
    })
  }, [pageData])

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta
          property="og:title"
          content="Decathlon a maior loja de artigos esportivos"
        />
      </Helmet>
      <UserDataProvider>
        <ThemeProvider>
          <GlobalContextProvider pageData={pageData}>
            <WishlistProvider>
              <CheckoutProvider paramOrderFormId={paramOrderFormId}>
                <CartFeedbackProvider>
                  <AddressProvider>
                    <SourceTracker />

                    {mode !== 'app' && !hideSiteHeader && <Header />}

                    <>{children}</>

                    {mode !== 'app' && !hideSiteFooter && (
                      <>{isAccountFooter ? <AccountFooter /> : <Footer />}</>
                    )}

                    <InitialPopUps isApp={mode === 'app'} />
                    {/* Browsers with JS enabled parse <noscript> content as raw
                    text rather than DOM nodes, so nesting real JSX here (an
                    <iframe>) makes the client's parsed DOM diverge from
                    React's tree and fails hydration (React error #418/#423).
                    dangerouslySetInnerHTML keeps this a leaf node React never
                    diffs against, matching the string it rendered on the server. */}
                    <noscript
                      dangerouslySetInnerHTML={{
                        __html:
                          '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-T777CVM" height="0" width="0" style="display:none;visibility:hidden" title="Google Tag Manager iframe"></iframe>',
                      }}
                    />

                    <noscript
                      dangerouslySetInnerHTML={{
                        __html:
                          '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-W9G828C4" height="0" width="0" style="display:none;visibility:hidden" title="Google Tag Manager iframe"></iframe>',
                      }}
                    />

                    <PixPaymentReminder />
                    <CartFeedback />
                    <ModalSheet />
                    <StatsigExposure />
                  </AddressProvider>
                </CartFeedbackProvider>
              </CheckoutProvider>
            </WishlistProvider>
          </GlobalContextProvider>
        </ThemeProvider>
      </UserDataProvider>
    </>
  )
}

/**
 * @public Consumida via fragment spread `...SiteInformation` em
 * ~36 arquivos de src/pages/** (extração de query em tempo de build
 * do Gatsby, não um import JS estático) - o knip não rastreia esse
 * mecanismo e reporta este export como não usado, mas removê-lo
 * quebra o build (`gatsby build` falha com "The fragment
 * 'SiteInformation' does not exist" em toda página que usa o
 * fragmento). SPDBR-5490 T5.22 (regressão pega no checkpoint de fim
 * da Fase 4 - ver STATE.md).
 */
export const siteInformationFragment = graphql`
  fragment SiteInformation on Site {
    siteMetadata {
      title
      titleTemplate
      description
      siteUrl
      ogImage
    }
  }
`

export default Layout
